import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Receipt } from 'lucide-react';
import { api } from '../../../lib/api';
import InvoiceStatusChip from '../../../components/crm/InvoiceStatusChip';
import EmptyState from '../../../components/crm/EmptyState';
import { fmtCents } from '../../../lib/financial';
import NewInvoiceDialog from '../../../components/admin/NewInvoiceDialog';
import RenewalPanel from './RenewalPanel';

// Admin-only financial tab on AccountDetail. Parent already gates tab
// visibility via role check before rendering this component.
export default function FinancialTab({ account }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [renewal, setRenewal] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/admin/financial/accounts/${account.id}`);
      setSummary(res.summary);
      setRenewal(res.renewal || null);
      setInvoices(res.invoices || []);
    } catch (err) {
      setError(err?.message || 'Failed to load financial summary');
    } finally {
      setLoading(false);
    }
  }, [account.id]);
  useEffect(() => { load(); }, [load]);

  const onCreated = async () => {
    await load();
    setNewOpen(false);
  };

  if (loading && invoices.length === 0) return <div className="animate-pulse h-16 rounded-lg bg-white/[0.03]" />;

  return (
    <div className="space-y-4">
      {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">{error}</div>}

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SummaryStat label="Billed" value={fmtCents(summary.billedCents)} />
          <SummaryStat label="Paid" value={fmtCents(summary.paidCents)} tone="good" />
          <SummaryStat label="Outstanding" value={fmtCents(summary.outstandingCents)} tone={summary.overdueCount > 0 ? 'alert' : null} />
        </div>
      )}
      {summary && summary.overdueCount > 0 && (
        <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          {summary.overdueCount} overdue invoice{summary.overdueCount === 1 ? '' : 's'}.
        </div>
      )}

      <RenewalPanel account={account} renewal={renewal} onChange={load} />

      <div className="flex items-center justify-between">
        <div className="text-[12px] text-gray-500">{invoices.length} invoice{invoices.length === 1 ? '' : 's'}</div>
        <button onClick={() => setNewOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900">
          <Plus className="w-3 h-3" /> New invoice
        </button>
      </div>

      {invoices.length === 0 ? (
        <EmptyState icon={Receipt} title="No invoices yet" />
      ) : (
        <div className="space-y-2">
          {invoices.map((inv) => (
            <button key={inv.id} onClick={() => navigate(`/admin/invoices/${inv.id}`)}
              className="w-full text-left p-3 rounded-xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] transition-colors flex items-center justify-between gap-3">
              <div>
                <div className="text-[13px] text-white font-mono">{inv.invoiceNumber}</div>
                <div className="text-[11px] text-gray-500">Issued {inv.issuedAt}{inv.dueAt ? ` · Due ${inv.dueAt}` : ''}</div>
              </div>
              <div className="flex items-center gap-3">
                <InvoiceStatusChip status={inv.status} />
                {inv.isOverdue && <span className="text-[10px] text-red-300 uppercase tracking-wider">overdue</span>}
                <div className="text-right">
                  <div className="text-[13px] text-white">{fmtCents(inv.amountCents, inv.currency)}</div>
                  <div className="text-[10px] text-gray-500">{fmtCents(inv.outstandingCents)} open</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <NewInvoiceDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        defaultAccountLocked
        prefill={{ account }}
        onCreated={onCreated}
      />
    </div>
  );
}

function SummaryStat({ label, value, tone = null }) {
  const toneCls = tone === 'alert' ? 'text-red-300' : tone === 'good' ? 'text-emerald-300' : 'text-white';
  return (
    <div className="p-3 rounded-xl border border-white/[0.05] bg-white/[0.015]">
      <div className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</div>
      <div className={`text-[16px] font-semibold mt-0.5 ${toneCls}`}>{value}</div>
    </div>
  );
}

